<template>
  <div class="report-stack">
    <p v-if="!reports.length" class="empty-copy">{{ emptyText }}</p>
    <button
      v-for="(report, index) in reports"
      :key="report.id"
      type="button"
      class="paper"
      :class="{
        selected: Number(report.id) === Number(selectedId),
        unread: !report.read_at,
        held: isDefenseReport(report) && defenseHeld(report),
        fallen: isDefenseReport(report) && !defenseHeld(report),
      }"
      :style="paperStyle(index)"
      @click="$emit('select', report)"
    >
      <span class="paper-kind">{{ kindLabel(report) }}</span>
      <span class="paper-title">{{ paperTitle(report) }}</span>
      <span class="paper-meta">{{ outcomeLine(report) }}</span>
    </button>
  </div>
</template>

<script setup>
import {
  defenseHeld,
  intelReportMeta,
  intelReportTitle,
  isDefenseReport,
} from "../utils/intelReports";

const props = defineProps({
  reports: {
    type: Array,
    default: () => [],
  },
  selectedId: {
    type: [Number, String],
    default: null,
  },
  emptyText: {
    type: String,
    default: "No intel reports yet.",
  },
});

defineEmits(["select"]);

function kindLabel(report) {
  if (isDefenseReport(report)) {
    return defenseHeld(report) ? "Defense Held" : "Defense Fallen";
  }
  return report?.kind === "counter_intel" ? "Counter-Intel" : "Scout Report";
}

function paperTitle(report) {
  if (isDefenseReport(report)) {
    return intelReportTitle(report);
  }
  if (report?.kind === "counter_intel") {
    return report.other_nickname || "Unknown empire";
  }
  return report?.city_name || report?.other_nickname || "Unknown city";
}

function outcomeLine(report) {
  if (isDefenseReport(report)) {
    return intelReportMeta(report);
  }
  const o = report?.outcomes || {};
  if (report?.kind === "counter_intel") {
    const n = Math.max(0, Math.floor(Number(o.captured) || 0));
    return n === 1 ? "1 scout captured" : `${n} scouts captured`;
  }
  const success = Math.max(0, Math.floor(Number(o.success) || 0));
  const killed = Math.max(0, Math.floor(Number(o.killed) || 0));
  const captured = Math.max(0, Math.floor(Number(o.captured) || 0));
  return `${success} returned · ${killed} killed · ${captured} captured`;
}

function paperStyle(index) {
  return {
    zIndex: props.reports.length - index,
    transform: `translate(${index * 6}px, ${index * 5}px)`,
  };
}
</script>

<style scoped>
.report-stack {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-height: 0;
}

.empty-copy {
  margin: 0;
  color: #8892b0;
  font-size: 0.8rem;
  font-style: italic;
}

.paper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  width: calc(100% - 18px);
  padding: 0.55rem 0.7rem;
  text-align: left;
  cursor: pointer;
  background: linear-gradient(180deg, #f4ead2 0%, #e2d3b1 100%);
  color: #2a2418;
  border: 1px solid #c9b48a;
  border-radius: 3px;
  box-shadow: 2px 3px 8px rgba(0, 0, 0, 0.35);
  font-family: "Exo 2", sans-serif;
}

.paper.unread {
  box-shadow:
    2px 3px 8px rgba(0, 0, 0, 0.35),
    0 0 10px rgba(0, 240, 255, 0.45);
}

.paper.selected,
.paper:hover {
  border-color: #00f0ff;
}

.paper-kind {
  font-family: "Cinzel", serif;
  font-size: 0.62rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6b5428;
}

.paper.held .paper-kind {
  color: #2f6b46;
}

.paper.fallen .paper-kind {
  color: #8b2e36;
}

.paper-title {
  font-weight: 700;
  font-size: 0.88rem;
}

.paper-meta {
  font-size: 0.7rem;
  color: #5c5344;
}
</style>
